// A request that outlasts a bad connection.
//
// On a phone the connection drops — a lift, a tunnel, the app sent to the
// background — and a request made then either fails at once or never
// answers. Left to itself the page waits for ever, or gives up with a toast,
// and the press that made the request is lost: "Undo does not respond". A
// request made through `persist` instead says how it is going — answering,
// slow, or no connection and trying again — tries again with a growing pause,
// at once when the phone says it is back online or the page is looked at
// again, and is given up only when a newer press takes its place.
//
// Only a failure of the connection is tried again: a request the server
// refused (a 4xx) is its answer, and is thrown as it was.

export type NetState =
  | { kind: "ok" }
  /** Asked, and no answer yet after a moment. */
  | { kind: "slow" }
  /** The connection failed; trying again at `retryAt` (ms since the epoch). */
  | { kind: "offline"; attempt: number; retryAt: number };

/** How long one attempt may take before it counts as a failed connection. */
export const ATTEMPT_MS = 12_000;
/** How long without an answer before the page says it is waiting. */
export const SLOW_MS = 1_500;

/** A newer press took the request's place. */
export class Superseded extends Error {
  constructor() {
    super("superseded");
  }
}

/** Whether a failure is the connection's, and worth trying again. */
export function isTransient(err: unknown): boolean {
  if (err instanceof Superseded) return false;
  // The server's own refusal (api.ts's RequestError) carries its status.
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status === "number") return status === 0 || status === 502 || status === 503 || status === 504;
  const name = (err as { name?: string } | null)?.name;
  // fetch throws a TypeError when there is no connection ("Load failed" in
  // Safari, "Failed to fetch" in Chrome), and an AbortError when the attempt
  // was cut off for taking too long.
  return name === "TypeError" || name === "AbortError" || name === "TimeoutError";
}

/** The pause before attempt `n` (1-based): 1 s, 2 s, 4 s, 8 s, then 10 s. */
export const backoff = (n: number): number => Math.min(10_000, 1000 * 2 ** Math.max(0, n - 1));

/** Resolves after `ms`, or at once when the phone is back online or the page
 *  is looked at again — whichever comes first. */
function pause(ms: number): Promise<void> {
  return new Promise((done) => {
    const target = globalThis as unknown as EventTarget & { document?: Document };
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      target.removeEventListener?.("online", finish);
      target.document?.removeEventListener?.("visibilitychange", onVisible);
      done();
    };
    const onVisible = () => {
      if (target.document?.visibilityState === "visible") finish();
    };
    const timer = setTimeout(finish, ms);
    target.addEventListener?.("online", finish);
    target.document?.addEventListener?.("visibilitychange", onVisible);
  });
}

/**
 * `send` until it answers. Each attempt gets an AbortSignal that cuts it off
 * after `attemptMs`. `onState` hears how it is going; `superseded` is asked
 * before every attempt and after every failure, and a true there throws
 * Superseded rather than trying again.
 */
export async function persist<T>(
  send: (signal: AbortSignal) => Promise<T>,
  { onState, superseded = () => false, attemptMs = ATTEMPT_MS, slowMs = SLOW_MS }: { onState?: (state: NetState) => void; superseded?: () => boolean; attemptMs?: number; slowMs?: number } = {}
): Promise<T> {
  for (let attempt = 1; ; attempt++) {
    if (superseded()) throw new Superseded();
    const controller = new AbortController();
    const cut = setTimeout(() => controller.abort(), attemptMs);
    const slow = attempt === 1 ? setTimeout(() => onState?.({ kind: "slow" }), slowMs) : null;
    // The attempt's own timers end with the attempt, before any pause: left
    // running, "slow" would be said again in the middle of trying again.
    const settled = () => {
      clearTimeout(cut);
      if (slow) clearTimeout(slow);
    };
    let failure: unknown;
    try {
      const answer = await send(controller.signal);
      settled();
      if (superseded()) throw new Superseded();
      onState?.({ kind: "ok" });
      return answer;
    } catch (err) {
      settled();
      failure = err;
    }
    if (superseded()) throw new Superseded();
    if (!isTransient(failure)) {
      onState?.({ kind: "ok" });
      throw failure;
    }
    const wait = backoff(attempt);
    onState?.({ kind: "offline", attempt, retryAt: Date.now() + wait });
    await pause(wait);
  }
}
